import React, { useEffect, useState, useMemo } from "react";
import { fetchJson } from "../../api";

const GOLD = "#d4a550";
const GOLD_DIM = "rgba(212,165,80,0.10)";
const GOLD_BORDER = "rgba(212,165,80,0.28)";
const SURFACE = "#0f0f17";
const SURFACE2 = "#13131e";
const BORDER = "rgba(255,255,255,0.07)";
const TEXT = "#e8e6df";
const TEXT_DIM = "rgba(232,230,223,0.45)";

export default function EpisodeAssignModal({ planId, episodes, blocksFlat, onClose, onSaved }) {
  const [squad, setSquad] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Episodio attivo oggi
  const todayStr = new Date().toISOString().slice(0, 10);
  const defaultEp = useMemo(() => {
    if (!episodes?.length) return 1;
    // Trova l'episodio con tx_date >= oggi più vicino
    const active = episodes.find(e => e.txDate >= todayStr);
    return active ? active.episodeNo : episodes[episodes.length - 1].episodeNo;
  }, [episodes, todayStr]);

  const [selectedEpNo, setSelectedEpNo] = useState(defaultEp);

  // Carica squadra
  useEffect(() => {
    fetchJson(`/postplans/${planId}/squad`)
      .then(d => setSquad(d.squad || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [planId]);

  // Fasi uniche (dalla squadra)
  const phases = useMemo(() => {
    const map = new Map();
    squad.forEach(r => {
      if (!map.has(r.phase_id)) map.set(r.phase_id, { id: r.phase_id, name: r.phase_name, sortOrder: r.sort_order });
    });
    return [...map.values()].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [squad]);

  // Blocchi dell'episodio selezionato
  const epBlocks = useMemo(() =>
    blocksFlat.filter(b => b.episodeNumber === selectedEpNo),
    [blocksFlat, selectedEpNo]
  );

  // Stato assegnazioni: { phaseId: [{lineId, partLabel}] }
  const [assignments, setAssignments] = useState({});

  // Inizializza assegnazioni dai blocchi esistenti
  useEffect(() => {
    const init = {};
    phases.forEach(ph => {
      const blocks = epBlocks.filter(b => String(b.phaseId) === String(ph.id));
      const phaseSquad = squad.filter(r => String(r.phase_id) === String(ph.id));
      // Usa i blocchi come base (definiscono quante linee sono attive per questo episodio)
      // e precompila con la persona della squad corrispondente
      if (blocks.length > 0) {
        init[ph.id] = blocks.map(b => {
          const matchedSquad = phaseSquad.find(r => Number(r.resource_no) === Number(b.lineNo));
          return {
            lineId: matchedSquad ? String(matchedSquad.line_id || matchedSquad.id) : String(b.lineId),
            partLabel: b.partLabel || "",
          };
        });
      } else {
        init[ph.id] = [];
      }
    });
    setAssignments(init);
  }, [selectedEpNo, phases, epBlocks, squad]);

  // Persone per fase
  function getSquadForPhase(phaseId) {
    return squad.filter(r => String(r.phase_id) === String(phaseId));
  }

  function setAssignment(phaseId, idx, field, value) {
    setAssignments(prev => {
      const arr = [...(prev[phaseId] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, [phaseId]: arr };
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const flat = [];
      Object.entries(assignments).forEach(([phaseId, arr]) => {
        arr.forEach(({ lineId, partLabel }) => {
          if (lineId) flat.push({ phaseId: Number(phaseId), lineId: Number(lineId), partLabel: partLabel || null });
        });
      });
      await fetchJson(`/postplans/${planId}/episode-assign`, {
        method: "POST",
        body: JSON.stringify({ episodeNo: selectedEpNo, assignments: flat }),
      });
      onSaved();
      onClose();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: SURFACE, border: `1px solid ${GOLD_BORDER}`, borderRadius: 12, width: 520, maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 10, fontFamily: "'Kanit', sans-serif", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, marginBottom: 2 }}>PostPlan</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>Gestione episodio</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: TEXT_DIM, cursor: "pointer", fontSize: 20 }}>×</button>
        </div>

        {/* Selettore episodio */}
        <div style={{ padding: "12px 20px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => setSelectedEpNo(n => Math.max(1, n - 1))}
            style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT_DIM, cursor: "pointer", padding: "4px 10px", fontSize: 13 }}>‹</button>
          <select value={selectedEpNo} onChange={e => setSelectedEpNo(Number(e.target.value))}
            style={{ flex: 1, background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 13, padding: "6px 10px", outline: "none", colorScheme: "dark" }}>
            {episodes.map(ep => (
              <option key={ep.episodeNo} value={ep.episodeNo}>EP.{ep.episodeNo}{ep.txDate ? ` — TX: ${ep.txDate}` : ""}</option>
            ))}
          </select>
          <button onClick={() => setSelectedEpNo(n => Math.min(episodes.length, n + 1))}
            style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT_DIM, cursor: "pointer", padding: "4px 10px", fontSize: 13 }}>›</button>
        </div>

        {/* Corpo */}
        <div style={{ overflowY: "auto", scrollbarWidth:"thin", scrollbarColor:"rgba(212,165,80,0.4) transparent", flex: 1, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
          {loading ? (
            <div style={{ textAlign: "center", color: TEXT_DIM, fontSize: 13, padding: 32 }}>Caricamento...</div>
          ) : phases.length === 0 ? (
            <div style={{ textAlign: "center", color: TEXT_DIM, fontSize: 13, padding: 32 }}>Nessuna squadra assegnata a questo piano.</div>
          ) : phases.map(ph => {
            const phaseSquad = getSquadForPhase(ph.id);
            const phaseAssignments = assignments[ph.id] || [];
            return (
              <div key={ph.id}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: TEXT_DIM, marginBottom: 8 }}>{ph.name}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {phaseAssignments.map((a, idx) => (
                    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 12px", background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 8 }}>
                      <span style={{ fontSize: 11, color: TEXT_DIM, minWidth: 16 }}>L{idx + 1}</span>
                      <select value={a.lineId || ""} onChange={e => setAssignment(ph.id, idx, "lineId", e.target.value)}
                        style={{ flex: 1, background: SURFACE, border: `1px solid ${a.lineId ? GOLD_BORDER : BORDER}`, borderRadius: 6, color: a.lineId ? TEXT : TEXT_DIM, fontSize: 12, padding: "5px 8px", outline: "none", colorScheme: "dark" }}>
                        <option value="">— Non assegnato —</option>
                        {phaseSquad.map(r => (
                          <option key={r.id} value={String(r.line_id || r.id)}>
                            {r.nome ? `${r.nome} ${r.cognome || ""}`.trim() : r.label || r.name_override || `L${r.resource_no}`}
                          </option>
                        ))}
                      </select>
                      <input value={a.partLabel || ""} onChange={e => setAssignment(ph.id, idx, "partLabel", e.target.value)}
                        placeholder="Parte / label"
                        style={{ width: 120, background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 12, padding: "5px 8px", outline: "none" }} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: `1px solid ${BORDER}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT_DIM, fontSize: 12, padding: "7px 16px", cursor: "pointer" }}>Annulla</button>
          <button onClick={handleSave} disabled={saving}
            style={{ background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, borderRadius: 6, color: GOLD, fontSize: 12, fontWeight: 700, padding: "7px 16px", cursor: saving ? "wait" : "pointer", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Salvataggio..." : "Salva e ricalcola"}
          </button>
        </div>
      </div>
    </div>
  );
}
